import React from 'react';
import { DetailPickerType } from './use-company-group-picker';
import { CustomFieldValue } from './custom-field-value';
import { SVGS } from '../../../assets/svgs';

interface DetailsTabContentProps {
  customerData: any;
  selectedLifeCycle: string[];
  selectedSegments: string[];
  selectedRoutes: string[];
  customFieldRows: [string, unknown][];
  onOpenPicker: (type: DetailPickerType) => void;
}

const getNonEmpty = (value: unknown, fallback = '-') => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text.length ? text : fallback;
};

export const DetailsTabContent: React.FC<DetailsTabContentProps> = ({
  customerData,
  selectedLifeCycle,
  selectedSegments,
  selectedRoutes,
  customFieldRows,
  onOpenPicker,
}) => {
  return (
    <div className="company-details-screen__tab-content">
      <div className="company-details-screen__section">
        <div className="company-details-screen__section-title font-semibold">
          Basic Details
        </div>
        <div className="company-details-screen__card">
          <InfoRow
            label="Company Name"
            value={getNonEmpty(customerData?.companyName)}
            icon={SVGS.FirmIcon}
          />
          <InfoRow
            label="Customer Name"
            value={getNonEmpty(customerData?.name)}
            icon={SVGS.UserIcon}
          />
          <InfoRow
            label="Phone Number"
            value={getNonEmpty(customerData?.telephone)}
            icon={SVGS.PhoneIconBlack}
          />
          <InfoRow
            label="Email ID"
            value={getNonEmpty(customerData?.email)}
            icon={SVGS.Envelope}
          />
          <InfoRow
            label="Customer Code"
            value={getNonEmpty(customerData?.distributorCode)}
            icon={SVGS.CustomerCode}
          />
          <InfoRow
            label="Language"
            value={getNonEmpty(customerData?.languageCode)}
            icon={SVGS.LanguageIcon}
          />
          <InfoRow
            label="Source"
            value={getNonEmpty(customerData?.importSource)}
            hideDivider
            icon={SVGS.CustomerCode}
          />
        </div>
      </div>
      <div className="company-details-screen__section">
        <div className="company-details-screen__section-title font-semibold">
          Life Cycle/Segments/Routes
        </div>
        <div className="company-details-screen__card">
          <TagRow
            title="Life Cycle"
            values={selectedLifeCycle}
            onAddClick={() => onOpenPicker('lifecycle')}
            icon={SVGS.SynchronizeIcon}
          />
          <TagRow
            title="Segments"
            values={selectedSegments}
            onAddClick={() => onOpenPicker('segments')}
            icon={SVGS.segmentIcon}
          />
          <TagRow
            title="Routes"
            values={selectedRoutes}
            onAddClick={() => onOpenPicker('routes')}
            hideDivider
            icon={SVGS.MapIcon}
          />
        </div>
      </div>
      <div className="company-details-screen__section">
        <div className="company-details-screen__section-title font-semibold">
          Address Details
        </div>
        <div className="company-details-screen__card">
          <InfoRow
            label="Physical Address"
            value={getNonEmpty(
              [
                customerData?.physicalAddress?.address1,
                customerData?.physicalAddress?.address2,
              ]
                .filter(Boolean)
                .join(', ')
            )}
            icon={SVGS.SegmentsIcon}
          />
          <InfoRow
            label="Shipping Address"
            value={getNonEmpty(
              [
                customerData?.shippingAddress?.shippingAddress1,
                customerData?.shippingAddress?.shippingAddress2,
                customerData?.shippingAddress?.city,
              ]
                .filter(Boolean)
                .join(', ')
            )}
            icon={SVGS.SegmentsIcon}
            hideDivider
          />
        </div>
      </div>
      <div className="company-details-screen__section">
        <div className="company-details-screen__section-title font-semibold">
          Custom Fields
        </div>
        <div className="company-details-screen__card">
          {customFieldRows.length ? (
            customFieldRows.map(([fieldName, fieldValue], index) => (
              <div key={fieldName}>
                <div className="company-details-screen__label font-regular">
                  {fieldName}
                </div>
                <CustomFieldValue value={fieldValue} />
                {index !== customFieldRows.length - 1 && (
                  <div className="company-details-screen__divider" />
                )}
              </div>
            ))
          ) : (
            <div className="company-details-screen__empty font-regular">
              No custom fields found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const InfoRow: React.FC<{
  label: string;
  value: string;
  icon?: string;
  hideDivider?: boolean;
}> = ({ label, value, icon, hideDivider = false }) => (
  <div className="company-details-screen__info-row">
    {icon && (
      <div className="company-details-screen__icon">
        <img src={icon} alt={label} />
      </div>
    )}
    <div className="company-details-screen__info-content-wrapper">
      <div className="company-details-screen__info-content">
        <div className="company-details-screen__label font-regular">
          {label}
        </div>
        <div className="company-details-screen__value font-semibold">
          {value}
        </div>
      </div>
      {!hideDivider && <div className="company-details-screen__divider" />}
    </div>
  </div>
);

const TagRow: React.FC<{
  title: string;
  values: string[];
  onAddClick?: () => void;
  hideDivider?: boolean;
  icon?: string;
}> = ({ title, values, onAddClick, hideDivider = false, icon }) => (
  <div className="company-details-screen__tag-row">
    {icon && (
      <div className="company-details-screen__icon">
        <img src={icon} alt={title} />
      </div>
    )}
    <div className="company-details-screen__info-content-wrapper">
      <div className="company-details-screen__tag-row-header">
        <div>
          <div className="company-details-screen__label font-regular">
            {title}
          </div>
          <div className="company-details-screen__chip-wrap company-details-screen__chip-wrap--inline">
            {values.length ? (
              <>
                {values.slice(0, 2).map((value) => (
                  <span
                    className="company-details-screen__chip font-regular"
                    key={value}
                  >
                    {value}
                  </span>
                ))}
                {values.length > 2 && (
                  <span className="company-details-screen__chip font-regular">
                    +{values.length - 2}
                  </span>
                )}
              </>
            ) : (
              <span className="company-details-screen__empty font-regular">
                -
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          className="company-details-screen__add-btn"
          onClick={onAddClick}
        >
          +
        </button>
      </div>
      {!hideDivider && <div className="company-details-screen__divider" />}
    </div>
  </div>
);
